import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase } from '../../creatives/creative-api';
import { isChannel, type Channel } from '../queries';
import { isQualityErrorCode } from './errors';
import type {
  BasisValue,
  QualityAnswer,
  QualityBreakdown,
  QualityCandidateCount,
  QualityChannelArrivalRow,
  QualityChannelCreditRow,
  QualityChannelPick,
  QualityCount,
  QualityCounts,
  QualityCoverage,
  QualityErrorCode,
  QualityFacet,
  QualityFacetAnswer,
  QualityFacetData,
  QualityFilters,
  QualityHome,
  QualityHomeDay,
  QualityHomeSpan,
  QualityHostCatalog,
  QualityHostRow,
  QualityLinkPick,
  QualityMatch,
  QualityMeta,
  QualityMetric,
  QualityNoHosts,
  QualityPage,
  QualityPageItem,
  QualityReport,
  QualityRequest,
  QualityScope,
  QualitySourceRow,
  QualitySpanEcho,
  QualityValueRow,
  QualityView,
} from './types';

/**
 * The relay refused or failed. `code` is null when the answer named no known code or never came (`status` 0), `hosts`
 * the addresses a `host_not_owned` answer names (null when it names none).
 */
export class QualityError extends Error {
  readonly code: QualityErrorCode | null;
  readonly status: number;
  readonly hosts: readonly string[] | null;
  constructor(code: QualityErrorCode | null, status: number, hosts: readonly string[] | null = null) {
    super(code ?? `HTTP ${status}`);
    this.name = 'QualityError';
    this.code = code;
    this.status = status;
    this.hosts = hosts;
  }
}

type Fields = Record<string, unknown>;
type Read<T> = (v: unknown) => T;

const malformed = (): never => {
  throw new Error('malformed traffic quality answer');
};
const isRecord = (v: unknown): v is Fields => typeof v === 'object' && v !== null && !Array.isArray(v);
const record: Read<Fields> = (v) => (isRecord(v) ? v : malformed());
const text: Read<string> = (v) => (typeof v === 'string' ? v : malformed());
const flag: Read<boolean> = (v) => (typeof v === 'boolean' ? v : malformed());
const real: Read<number> = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : malformed());
const utc: Read<string> = (v) => {
  const value = text(v);
  return value.endsWith('Z') && !Number.isNaN(Date.parse(value)) ? value : malformed();
};
/** Counts arrive as decimal strings. */
const count: Read<number> = (v) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : malformed());
const oneOf =
  <T extends string>(...values: T[]): Read<T> =>
  (v) =>
    values.find((value) => value === v) ?? malformed();
/** A nullable field the relay left out reads as null. */
const orNull =
  <T>(read: Read<T>): Read<T | null> =>
  (v) =>
    v === null || v === undefined ? null : read(v);
const list =
  <T>(read: Read<T>): Read<T[]> =>
  (v) =>
    Array.isArray(v) ? v.map(read) : malformed();
/** `{[key]: read(v)}`, or nothing when the answer left the field out. */
const optional = <K extends string, T>(key: K, v: unknown, read: Read<T>): { [P in K]?: T } =>
  v === null || v === undefined ? {} : ({ [key]: read(v) } as { [P in K]?: T });

const maybeCount: Read<QualityCount> = orNull(count);
const texts = list(text);
const mode = oneOf('in', 'out');
const channel: Read<Channel> = (v) => {
  const name = text(v);
  return isChannel(name) ? name : malformed();
};
/** A row of a channel this client does not know is left out, as the legacy reader leaves it out. */
const channelRows =
  <T>(read: (row: Fields, channel: Channel) => T): Read<T[]> =>
  (v) =>
    list(record)(v).flatMap((row) => {
      const name = text(row.channel);
      return isChannel(name) ? [read(row, name)] : [];
    });

const sourceRows = channelRows(
  (row, name): QualitySourceRow => ({
    channel: name,
    platform: text(row.platform),
    creditedPeople: maybeCount(row.creditedVisitors),
    arrivalPeople: maybeCount(row.arrivalVisitors),
  }),
);
const creditRows = channelRows(
  (row, name): QualityChannelCreditRow => ({ channel: name, creditedPeople: maybeCount(row.creditedVisitors) }),
);
const arrivalRows = channelRows(
  (row, name): QualityChannelArrivalRow => ({ channel: name, arrivalPeople: maybeCount(row.arrivalVisitors) }),
);

const matchOf: Read<QualityMatch> = (v) => {
  const o = record(v);
  return { mode: mode(o.mode), values: texts(o.values) };
};
const pageItemOf: Read<QualityPageItem> = (v) => {
  const o = record(v);
  return { kind: oneOf('site', 'landing')(o.kind), path: text(o.path) };
};
const channelPickOf: Read<QualityChannelPick> = (v) => {
  const o = record(v);
  return {
    channel: channel(o.channel),
    platforms: o.platforms === 'all' ? 'all' : texts(o.platforms),
    except: texts(o.except),
  };
};
const linksOf: Read<QualityLinkPick> = (v) => {
  const o = record(v);
  return {
    affiliates: list((a) => {
      const affiliate = record(a);
      return { id: text(affiliate.id), exceptLinks: texts(affiliate.exceptLinks) };
    })(o.affiliates),
    exact: texts(o.exact),
    noLink: flag(o.noLink),
    ...optional('allExcept', o.allExcept, texts),
    ...optional('exceptAffiliates', o.exceptAffiliates, texts),
  };
};
const filtersOf: Read<QualityFilters> = (v) => {
  const o = record(v);
  return {
    ...optional('pages', o.pages, (p) => {
      const pages = record(p);
      return { mode: mode(pages.mode), items: list(pageItemOf)(pages.items) };
    }),
    ...optional('channels', o.channels, list(channelPickOf)),
    ...optional('links', o.links, linksOf),
    ...optional('campaigns', o.campaigns, matchOf),
    ...optional('countries', o.countries, matchOf),
  };
};

const metaOf: Read<QualityMeta> = (v) => {
  const o = record(v);
  const refresh = record(o.refreshObservation);
  const identityRefresh = record(o.identityRefreshObservation);
  const identityStatus = oneOf('observed', 'unavailable')(identityRefresh.status);
  const identityModel = text(o.identityModel);
  if (
    o.responseVersion !== 3 ||
    o.countingUnit !== 'person' ||
    o.population !== 'unflagged' ||
    identityModel.length === 0
  )
    malformed();
  const identityRefreshObservation =
    identityStatus === 'observed'
      ? {
          status: 'observed' as const,
          lastSuccessfulRefreshStartedAt: utc(identityRefresh.lastSuccessfulRefreshStartedAt),
        }
      : identityRefresh.lastSuccessfulRefreshStartedAt === undefined
        ? { status: 'unavailable' as const }
        : malformed();
  return {
    responseVersion: 3,
    countingUnit: 'person',
    population: 'unflagged',
    identityModel,
    identityRefreshObservation,
    requestId: text(o.requestId),
    policyVersion: text(o.policyVersion),
    rulesVersion: text(o.rulesVersion),
    registryCatalogAt: orNull(text)(o.registryCatalogAt),
    generatedAt: text(o.generatedAt),
    accountEvidence: { nativeTest: oneOf('available', 'unavailable')(record(o.accountEvidence).nativeTest) },
    refreshObservation: {
      status: oneOf('observed', 'unavailable')(refresh.status),
      lastSuccessfulRefreshStartedAt: orNull(text)(refresh.lastSuccessfulRefreshStartedAt),
    },
    snapshot: oneOf('single', 'mixed')(o.snapshot),
    verifiedBots: text(o.verifiedBots),
    staff: text(o.staff),
    pairedSiteLanding: oneOf('enabled', 'disabled')(o.pairedSiteLanding),
  };
};
const hostRowOf: Read<QualityHostRow> = (v) => {
  const o = record(v);
  return {
    host: text(o.host),
    kind: oneOf('site', 'landing', 'unknown')(o.kind),
    exposure: oneOf('public', 'internal', 'unclassified')(o.exposure),
    reason: orNull(oneOf('not_in_registry', 'claimed_elsewhere', 'purpose_conflict'))(o.reason),
    selected: flag(o.selected),
  };
};
const catalogOf: Read<QualityHostCatalog> = (v) => {
  const o = record(v);
  return {
    status: oneOf('fresh', 'stale', 'unavailable')(o.status),
    defaultBasis: oneOf('catalogue', 'internal', 'none')(o.defaultBasis),
  };
};
const envelopeOf = (o: Fields) => ({
  meta: metaOf(o.meta),
  hosts: list(hostRowOf)(o.hosts),
  hostCatalog: catalogOf(o.hostCatalog),
});
/** The common scope, which must echo the view that was asked. */
const scopeOf = <V extends QualityView>(o: Fields, view: V): QualityScope & { view: V } => {
  if (o.view !== view) malformed();
  return {
    tenantId: text(o.tenantId),
    environment: text(o.environment),
    view,
    selectedHosts: texts(o.selectedHosts),
    range: text(o.range),
  };
};
const spanOf: Read<QualitySpanEcho> = (v) => {
  const o = record(v);
  return { from: text(o.from), to: text(o.to) };
};

const basis = oneOf<BasisValue>('none', 'arrival', 'credit', 'empty', 'unavailable');
const metricOf: Read<QualityMetric> = (v) => {
  const o = record(v);
  return {
    pageViews: count(o.pageViews),
    visits: maybeCount(o.visits),
    people: maybeCount(o.visitors),
    activeMs: maybeCount(o.activeMs),
    engagementSessions: count(o.engagementSessions),
    averageSeconds: orNull(real)(o.averageSeconds),
  };
};
const countsOf: Read<QualityCounts> = (v) => {
  const o = record(v);
  return { pageViews: count(o.pageViews), visits: count(o.visits), people: count(o.visitors) };
};
const breakdownOf: Read<QualityBreakdown> = (v) => {
  const o = record(v);
  const overlap = record(o.visitorOverlap);
  return {
    primary: countsOf(o.primary),
    excluded: countsOf(o.excluded),
    all: countsOf(o.all),
    confirmedTest: countsOf(o.confirmedTest),
    verifiedBot: countsOf(o.verifiedBot),
    staff: countsOf(o.staff),
    suspected: countsOf(o.suspected),
    other: countsOf(o.other),
    peopleOverlap: {
      primaryOnly: count(overlap.primaryOnly),
      primaryAndExcluded: count(overlap.primaryAndExcluded),
      excludedOnly: count(overlap.excludedOnly),
    },
  };
};
const trendCountsOf = (v: unknown) => {
  const o = record(v);
  return { pageViews: count(o.pageViews), people: maybeCount(o.visitors) };
};
const pageOf: Read<QualityPage> = (v) => {
  const o = record(v);
  return {
    path: text(o.path),
    pageViews: count(o.pageViews),
    people: maybeCount(o.visitors),
    activeMs: maybeCount(o.activeMs),
    pageAverageSeconds: orNull(real)(o.pageAverageSeconds),
    suspectedPageViews: count(o.suspectedPageViews),
  };
};
const valueRowOf: Read<QualityValueRow> = (v) => {
  const o = record(v);
  return { value: text(o.value), people: maybeCount(o.visitors) };
};
const coverageOf: Read<QualityCoverage> = (v) => {
  const o = record(v);
  return {
    logicalEvents: count(o.logicalEvents),
    missingVisitIdEvents: count(o.missingVisitIdEvents),
    missingActiveMsEvents: count(o.missingActiveMsEvents),
    negativeActiveMsEvents: count(o.negativeActiveMsEvents),
    clockSkewEvents: count(o.clockSkewEvents),
    visitsNearHorizonEdge: count(o.visitsNearHorizonEdge),
  };
};

const reportOf = (o: Fields): QualityReport => {
  const scope = record(o.scope);
  const current = record(o.current);
  const previous = record(o.previous);
  const cards = record(current.cards);
  const filterBasis = record(scope.filterBasis);
  return {
    ...envelopeOf(o),
    scope: {
      ...scopeOf(scope, 'report'),
      current: spanOf(scope.current),
      previous: spanOf(scope.previous),
      timeBucket: oneOf('hour', 'day')(scope.timeBucket),
      filters: filtersOf(scope.filters),
      filterBasis: {
        pageViews: basis(filterBasis.pageViews),
        activeTime: basis(filterBasis.activeTime),
        visits: basis(filterBasis.visits),
        people: basis(filterBasis.visitors),
      },
    },
    current: {
      cards: { primary: metricOf(cards.primary), suspected: metricOf(cards.suspected) },
      breakdown: breakdownOf(current.breakdown),
      trend: list((p) => {
        const point = record(p);
        return {
          start: text(point.start),
          primary: trendCountsOf(point.primary),
          unflagged: trendCountsOf(point.unflagged),
          suspected: trendCountsOf(point.suspected),
          excluded: trendCountsOf(point.excluded),
        };
      })(current.trend),
      topPages: list(pageOf)(current.topPages),
      devices: list(valueRowOf)(current.devices),
      systems: list(valueRowOf)(current.systems),
      hosts: list((h) => {
        const host = record(h);
        return { host: text(host.host), pageViews: count(host.pageViews), people: maybeCount(host.visitors) };
      })(current.hosts),
      coverage: coverageOf(current.coverage),
      sources: sourceRows(current.sources),
      channels: creditRows(current.channels),
      sourcesTruncated: flag(current.sourcesTruncated),
    },
    previous: {
      cards: { primary: metricOf(record(previous.cards).primary) },
      topPages: list((p) => {
        const page = record(p);
        return { path: text(page.path), pageViews: count(page.pageViews) };
      })(previous.topPages),
      devices: list(valueRowOf)(previous.devices),
      systems: list(valueRowOf)(previous.systems),
      hosts: list((h) => {
        const host = record(h);
        return { host: text(host.host), people: maybeCount(host.visitors) };
      })(previous.hosts),
      sources: sourceRows(previous.sources),
      channels: creditRows(previous.channels),
    },
  };
};

const candidatesOf = orNull(
  list((c): QualityCandidateCount => {
    const o = record(c);
    return { id: text(o.id), people: count(o.visitors) };
  }),
);
/** The facet's rows, which must be the facet (and page kind) that was asked. */
const facetDataOf = (v: unknown, asked: QualityFacet): QualityFacetData => {
  const o = record(v);
  if (o.name !== asked.name) malformed();
  switch (asked.name) {
    case 'page': {
      const kind = oneOf('site', 'landing')(o.kind);
      if (kind !== asked.kind) malformed();
      return {
        name: 'page',
        kind,
        rows: list((r) => {
          const row = record(r);
          return { kind: oneOf('site', 'landing')(row.kind), path: text(row.path), pageViews: count(row.pageViews) };
        })(o.rows),
        truncated: flag(o.truncated),
      };
    }
    case 'source':
      return {
        name: 'source',
        rows: sourceRows(o.rows),
        channelArrivals: arrivalRows(o.channelArrivals),
        truncated: flag(o.truncated),
      };
    case 'link':
      return {
        name: 'link',
        rows: list((r) => {
          const row = record(r);
          return {
            affId: text(row.affId),
            creditedPeople: maybeCount(row.creditedVisitors),
            arrivalPeople: maybeCount(row.arrivalVisitors),
          };
        })(o.rows),
        truncated: flag(o.truncated),
      };
    case 'link_groups':
      return {
        name: 'link_groups',
        affiliates: candidatesOf(o.affiliates),
        affiliatesTruncated: flag(o.affiliatesTruncated),
        campaigns: candidatesOf(o.campaigns),
        campaignsTruncated: flag(o.campaignsTruncated),
      };
    case 'country':
      return {
        name: 'country',
        rows: list((r) => {
          const row = record(r);
          return { country: text(row.country), people: maybeCount(row.visitors) };
        })(o.rows),
        truncated: flag(o.truncated),
      };
  }
};
const facetAnswerOf = (o: Fields, asked: QualityFacet): QualityFacetAnswer => {
  const scope = record(o.scope);
  return {
    ...envelopeOf(o),
    scope: { ...scopeOf(scope, 'facet'), ...optional('filters', scope.filters, filtersOf) },
    facet: facetDataOf(o.facet, asked),
  };
};

const homeSpanOf: Read<QualityHomeSpan> = (v) => {
  const o = record(v);
  return { from: text(o.from), to: text(o.to), people: maybeCount(o.visitors) };
};
const homeDayOf: Read<QualityHomeDay> = (v) => {
  const o = record(v);
  return { start: text(o.start), people: count(o.visitors) };
};
const homeOf = (o: Fields): QualityHome => {
  const scope = record(o.scope);
  const home = record(o.home);
  const totals = record(home.totals);
  return {
    ...envelopeOf(o),
    scope: {
      ...scopeOf(scope, 'home_visitors'),
      ...optional('seriesWindow', scope.seriesWindow, (w) => {
        const dates = record(w);
        return {
          fromDate: text(dates.fromDate),
          throughDate: text(dates.throughDate),
          from: text(dates.from),
          to: text(dates.to),
        };
      }),
    },
    home: {
      totals: { current: homeSpanOf(totals.current), previous: homeSpanOf(totals.previous) },
      series: orNull((s) => {
        const series = record(s);
        return {
          bucket: oneOf('day')(series.bucket),
          from: text(series.from),
          to: text(series.to),
          points: list(homeDayOf)(series.points),
        };
      })(home.series),
      dayBars: orNull((b) => {
        const bars = record(b);
        return { current: homeDayOf(bars.current), previous: homeDayOf(bars.previous) };
      })(home.dayBars),
    },
  };
};

const viewOf = (o: Fields, asked: QualityRequest): QualityReport | QualityFacetAnswer | QualityHome => {
  switch (asked.view) {
    case 'report':
      return reportOf(o);
    case 'facet':
      return facetAnswerOf(o, asked.facet);
    case 'home_visitors':
      return homeOf(o);
  }
};

/** The same hosts in any order: Spark answers an explicit selection in its own resolved order. */
const sameHosts = (answered: readonly string[], asked: readonly string[]): boolean => {
  const wanted = new Set(asked);
  return new Set(answered).size === wanted.size && answered.every((host) => wanted.has(host));
};

/**
 * The answer for `asked`, which must be scoped to the tenant asked and to an explicit selection, or the relay's own
 * no-address answer, which carries no envelope.
 */
const decode = (
  data: unknown,
  tenantId: number,
  asked: QualityRequest,
): QualityReport | QualityFacetAnswer | QualityHome | QualityNoHosts => {
  const o = record(data);
  if (Array.isArray(o.hosts) && o.hosts.length === 0 && o.meta === undefined) return { hosts: [] };
  const answer = viewOf(o, asked);
  if (answer.scope.tenantId !== String(tenantId)) malformed();
  if (asked.selectedHosts && !sameHosts(answer.scope.selectedHosts, asked.selectedHosts)) malformed();
  return answer;
};

const refusedHosts = (v: unknown): string[] | null =>
  Array.isArray(v) && v.length > 0 && v.every((host): host is string => typeof host === 'string') ? v : null;

const abortReason = (signal: AbortSignal): unknown => signal.reason ?? new DOMException('aborted', 'AbortError');

/**
 * Spark answers within about 38 seconds (18 for its hosts and queue, 20 upstream). A request still unanswered after
 * this is given up, so a stalled connection cannot hold the tenant's queue.
 */
const ANSWER_TIMEOUT_MS = 45_000;

/**
 * One request, run to its end. The caller's abort never cancels it: Spark cannot see a disconnect and keeps running
 * it, so the tenant's queue must wait for it too. Once it ends, an aborted caller gets the abort, never the answer.
 */
const askOnce = async <R extends QualityRequest>(
  url: string,
  tenantId: number,
  body: R,
  signal?: AbortSignal,
): Promise<QualityAnswer<R> | QualityNoHosts> => {
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), ANSWER_TIMEOUT_MS);
  let res: { status: number; data: unknown };
  try {
    res = await axios.post<unknown>(url, body, creativesRequestConfig(timeout.signal));
  } catch {
    if (signal?.aborted) throw abortReason(signal);
    // No answer, given up or not: the generic failure (`query_timeout` is the service's own code, never ours).
    throw new QualityError(null, 0);
  } finally {
    clearTimeout(timer);
  }
  if (signal?.aborted) throw abortReason(signal);
  if (res.status < 200 || res.status >= 300) {
    const data = isRecord(res.data) ? res.data : {};
    const code = typeof data.code === 'string' && isQualityErrorCode(data.code) ? data.code : null;
    throw new QualityError(code, res.status, code === 'host_not_owned' ? refusedHosts(data.hosts) : null);
  }
  try {
    return decode(res.data, tenantId, body) as QualityAnswer<R> | QualityNoHosts;
  } catch {
    throw new QualityError(null, res.status);
  }
};

/** Resolves after `ms`, or rejects with the abort as soon as `signal` aborts. */
const pause = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const stop = (): void => {
      clearTimeout(timer);
      if (signal) reject(abortReason(signal));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', stop);
      resolve();
    }, ms);
    signal?.addEventListener('abort', stop, { once: true });
  });

export type QualityPostOptions = {
  /** Called once when a busy answer is to be asked again, as the wait before it starts. */
  onRetry?: () => void;
};

/**
 * Ask one quality question for the brand and decode the answer, or `{hosts: []}` when the brand has no address.
 * Only `analytics_busy` is asked again, once, after 1 to 2 seconds, and never after `signal` aborted. `signal` never
 * cancels a sent request: the call settles when that request ends (run it through `enqueueQuality`, whose wait ends
 * at the abort), and an abort ends only the wait before asking again. An abort rejects with the signal's reason, never
 * with a {@link QualityError}.
 */
export const postTrafficQuality = async <R extends QualityRequest>(
  tenantId: number,
  body: R,
  signal?: AbortSignal,
  options: QualityPostOptions = {},
): Promise<QualityAnswer<R> | QualityNoHosts> => {
  const base = creativesServiceBase();
  // With no service address the owner token would go to a path on this site.
  if (base === '') throw new QualityError('analytics_unconfigured', 0);
  const url = `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/website-analytics/traffic-quality`;
  try {
    return await askOnce(url, tenantId, body, signal);
  } catch (err) {
    if (!(err instanceof QualityError) || err.code !== 'analytics_busy' || signal?.aborted) throw err;
  }
  options.onRetry?.();
  await pause(1000 + Math.random() * 1000, signal);
  return askOnce(url, tenantId, body, signal);
};
